import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Image } from 'react-bootstrap';
import { axiosClient } from '../../library/Service/axiosClient';
import { useLocation } from 'react-router-dom';

const useQuery = () => new URLSearchParams(useLocation().search);
type Alumni = {
    uuid: string;
    nAME?: string;
    name?: string;
    firstName?: string;
    lastName?: string;
    profilePictureFilePath?: string | null;
    passOutYear?: number | string | null;
    programName?: string | null;
};

type Errors = {
    searchText?: string;
    captcha?: string;
    activityDate?: string;
    category?: string;
    mode?: string;
    description?: string;

    manualName?: string;
    manualEmail?: string;
    manualPhone?: string;
    manualPassOutYear?: string;
    manualProgramName?: string;
};

type Props = {
    className?: string;
};
const decodeId = (encoded: any) => {
    return atob(encoded); // Base64 decode
};
const AddNewFormModal: React.FC<Props> = () => {
    const [instituteName, setInstituteName] = useState('Brainware University');
    const [instituteLogo, setInstituteLogo] = useState(
        'https://ags-qa-bucket.s3.ap-south-1.amazonaws.com/8c9009e6-c4f2-4d85-b292-3ccd678e49eb_BRAINWARE-logo.jpg.webp'
    );

    const query = useQuery();
    const id = query.get('kdjkjjdkfhkjskjd'); // e.g. "10000005493" (string)

    const [searchText, setSearchText] = useState('');
    const [searchResults, setSearchResults] = useState<Alumni[]>([]);
    const [selectedAlumni, setSelectedAlumni] = useState<Alumni | null>(null);

    // dropdown visibility
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    // manual entry toggle
    const [isManualEntry, setIsManualEntry] = useState(false);

    // manual fields
    const [manualName, setManualName] = useState('');
    const [manualEmail, setManualEmail] = useState('');
    const [manualPhone, setManualPhone] = useState('');
    const [manualPassOutYear, setManualPassOutYear] = useState('');
    const [manualProgramName, setManualProgramName] = useState('');
const [isSearching, setIsSearching] = useState(false);
const [isSubmitting, setIsSubmitting] = useState(false);
    const [activityDate, setActivityDate] = useState('');
    const [category, setCategory] = useState('');
    const [mode, setMode] = useState('');
    const [description, setDescription] = useState('');

    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState<Errors>({});

    // ✅ show success / error instead of alert
    const [successMsg, setSuccessMsg] = useState('');
    const [submitError, setSubmitError] = useState('');

    const debounceRef = useRef<number | null>(null);

    const clearDebounce = () => {
        if (debounceRef.current) {
            window.clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
    };
    useEffect(() => {
        const today = new Date().toISOString().split('T')[0]; // yyyy-mm-dd
        setActivityDate(today);
    }, []);
    const todayDate = new Date().toISOString().split('T')[0];
    const schoolIdFromUrl = decodeId(id);
    // console.log(schoolIdFromUrl);
    // const encodeId = (id:any) => {
    //   return btoa(id.toString()); // Base64 encode
    // };

    // const encrypted = encodeId('10000005493');
    // console.log(encrypted); // Example: NzgzODc0OA==
    useEffect(() => {
        const fetchInstitute = async () => {
            const numericId = schoolIdFromUrl ? Number(schoolIdFromUrl) : NaN;
            if (!schoolIdFromUrl || Number.isNaN(numericId)) return;

            try {
                // ✅ replace this endpoint with your actual route
                const requestUrl = `${process.env.REACT_APP_BROADCAST_SERVER}/pre-signup/get-institute-name`;

                const resp = await axiosClient().post(
                    requestUrl,
                    { id: [numericId] }, // your SQL uses IN (?) so passing array is safe
                    { timeout: 20000 }
                );

                const row = resp?.data?.result?.[0];
                if (resp?.data?.success && row) {
                    if (row?.name) setInstituteName(String(row.name));
                    if (row?.logo) setInstituteLogo(String(row.logo)); // base64 or url
                }
            } catch (e) {
                console.error('Institute API error:', e);
            }
        };

        fetchInstitute();
    }, [schoolIdFromUrl]);

    const resetForm = () => {
        setSearchText('');
        setSearchResults([]);
        setSelectedAlumni(null);
        setCaptchaInput('');
        generateCaptcha();
        setIsDropdownOpen(false);

        setIsManualEntry(false);
        setManualName('');
        setManualEmail('');
        setManualPhone('');
        setManualPassOutYear('');
        setManualProgramName('');

        setActivityDate('');
        setCategory('');
        setMode('');
        setDescription('');
        setErrors({});
        clearDebounce();
    };

    const { added_by_user_id, added_by_name } = useMemo(() => {
        const userDataString = sessionStorage.getItem('user');
        if (!userDataString)
            return {
                added_by_user_id: null as string | null,
                added_by_name: null as string | null,
            };

        try {
            const u = JSON.parse(userDataString) as {
                userId?: string;
                firstName?: string;
                lastName?: string;
            };
            const name = `${u.firstName || ''} ${u.lastName || ''}`.trim();
            return {
                added_by_user_id: u.userId || null,
                added_by_name: name || null,
            };
        } catch {
            return { added_by_user_id: null, added_by_name: null };
        }
    }, []);
    // ✅ Simple captcha (no module)
    const [captchaText, setCaptchaText] = useState('');
    const [captchaInput, setCaptchaInput] = useState('');

    const generateCaptcha = () => {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // removed confusing I,O,1,0
        let out = '';
        for (let i = 0; i < 5; i++) out += chars.charAt(Math.floor(Math.random() * chars.length));
        setCaptchaText(out);
        setCaptchaInput('');
        setErrors((p) => ({ ...p, captcha: undefined }));
    };

    useEffect(() => {
        generateCaptcha(); // generate once on load
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getAlumniDisplayName = (a: Alumni) => a.nAME || a.name || '';
    const getMetaLine = (a: Alumni) => {
        const program = (a.programName || '').trim();
        const year = a.passOutYear ?? '';

        if (program && year) return `Program:- ${program}, Batch:- ${year}`;
        if (program) return program;
        if (year) return `${year}`;
        return 'Batch';
    };

    const getUuid = (a: Alumni | null) => (a?.uuid || '').trim();

    const doSearch = async (value: string) => {
        
        const v = value.trim();
        if (!v) {
            setSearchResults([]);
            return;
        }

       setIsSearching(true);
        try {
            const schoolId = schoolIdFromUrl ? [Number(schoolIdFromUrl)] : [];

            const criteria = {
                context: { searchBy: 'default', level: 'MY_SCHOOL' },
                name: v,
                passOutYear: [],
                educationOrEmploymentCurrentStatusCode: [],
                fieldOfStudyCode: [],
                universityId: [],
                pgUniversityId: [],
                collegeCountryCode: [],
                collegeCityCode: [],
                residingCountryCode: [],
                residingCityCode: [],
                entranceTestCode: [],
                skills: [],
                interests: [],
                programId: [],
                jobCompanyId: [],
                functionalAreaCode: [],
                industryCode: [],
                workingStatusCode: [],
                typeOfPracticeCode: [],
                jobRoleCode: [],
                defenceServiceBranchCode: [],
                seniorityLevelCode: [],
                higherStudyAtUG: null,
                schoolId,
                ugCollegeExists: true,
                userTypeCode: 'ALUMNI',
            };

            const url = `${process.env.REACT_APP_INTERNAL_ADMIN_SERVER}/search/all-alumni-list`;
            const response = await axiosClient().post(url, criteria, { timeout: 20000 });
            setSearchResults((response?.data || []) as Alumni[]);
        } catch (err) {
            console.error('Search API error:', err);
            setSearchResults([]);
        } finally {
           setIsSearching(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchText(value);

        // if user starts typing, assume they want normal search (not manual)
        if (isManualEntry) {
            setIsManualEntry(false);
            setManualName('');
            setManualEmail('');
            setManualPhone('');
            setManualPassOutYear('');
            setManualProgramName('');
        }

        setIsDropdownOpen(true);

        clearDebounce();
        debounceRef.current = window.setTimeout(() => doSearch(value), 300);
    };

    const handleSelect = (alumni: Alumni) => {
        setSelectedAlumni(alumni);
        setSearchText(getAlumniDisplayName(alumni));
        setSearchResults([]);

        setIsManualEntry(false);
        setIsDropdownOpen(false);

        setSubmitError('');
        setSuccessMsg('');

        setErrors((prev) => ({ ...prev, searchText: undefined }));
    };

    const handleManualEntry = () => {
        setIsManualEntry(true);

        // clear alumni selection
        setSelectedAlumni(null);

        // close dropdown & stop overlapping
        setSearchResults([]);
        setIsDropdownOpen(false);

        // show label in input
        setSearchText('Manual Entry');

        setSubmitError('');
        setSuccessMsg('');

        setErrors((prev) => ({
            ...prev,
            searchText: undefined,
            manualName: undefined,
            manualEmail: undefined,
            manualPhone: undefined,
            manualPassOutYear: undefined,
            manualProgramName: undefined,
        }));
    };

    const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

    const normalizePhone = (raw: string) => raw.replace(/[^\d]/g, '');
    const validatePhone = (raw: string) => {
        const digits = normalizePhone(raw);
        return digits.length >= 10 && digits.length <= 15;
    };

    const validate = () => {
        const temp: Errors = {};

        if (!isManualEntry && !selectedAlumni) temp.searchText = 'Please select an alumni (or choose Manual Entry)';

        if (isManualEntry) {
            if (!manualName.trim()) temp.manualName = 'Name is required';

            if (!manualEmail.trim()) temp.manualEmail = 'Email is required';
            else if (!isValidEmail(manualEmail)) temp.manualEmail = 'Enter a valid email';

            if (!manualPhone.trim()) temp.manualPhone = 'Phone number is required';
            else if (!validatePhone(manualPhone)) temp.manualPhone = 'Enter a valid phone number (10–15 digits)';

            if (!manualPassOutYear.trim()) temp.manualPassOutYear = 'Passout Year is required';
            else if (!/^\d{4}$/.test(manualPassOutYear.trim())) temp.manualPassOutYear = 'Enter a valid year (e.g., 2021)';

            if (!manualProgramName.trim()) temp.manualProgramName = 'Program Name is required';
        }
        // ✅ Captcha validation
        if (!captchaInput.trim()) temp.captcha = 'Captcha is required';
        else if (captchaInput.trim().toUpperCase() !== captchaText) temp.captcha = 'Captcha does not match';

        if (!activityDate) temp.activityDate = 'Activity Date is required';
        if (!category) temp.category = 'Category is required';
        if (!mode) temp.mode = 'Mode is required';

        const desc = (description || '').trim();
        if (!desc) temp.description = 'Description is required';
        else if (desc.length < 50) temp.description = 'Description must be at least 50 characters';
        else if (desc.length > 250) temp.description = 'Description must not exceed 250 characters';

        setErrors(temp);
        return Object.keys(temp).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        setSubmitError('');
        setSuccessMsg('');

        // close dropdown to avoid UI oddities
        setIsDropdownOpen(false);

        if (!validate()) return;

        try {
            setIsSubmitting(true);

            // ✅ MANUAL ENTRY
            if (isManualEntry) {
                const payload = {
                    name: manualName.trim(),
                    email_id: manualEmail.trim(),
                    phone_number: manualPhone.trim(),
                    passOutYear: manualPassOutYear.trim(),
                    program_name: manualProgramName.trim(),
                    activityDate,
                    category,
                    mode,
                    description: description.trim(),
                    added_by_user_id,
                    added_by_name,
                };

                const requestUrl = `${process.env.REACT_APP_BROADCAST_SERVER}/activity-log/add-manual-data?id=${schoolIdFromUrl}`;
                const response = await axiosClient().post(requestUrl, payload, { timeout: 20000 });

                if (response?.data?.success === false) {
                    setSubmitError(response?.data?.message || 'Failed to save manual activity.');
                    return;
                }

                setSuccessMsg('✅ Activity saved successfully (Manual Entry)!');
                resetForm();
                return;
            }

            // ✅ SELECTED ALUMNI ENTRY
            const uuid = getUuid(selectedAlumni);
            if (!uuid) {
                setSubmitError('Please select a valid alumni from the dropdown.');
                return;
            }

            const payload = {
                uuid,
                activityDate,
                category,
                mode,
                description: description.trim(),
                added_by_user_id,
                added_by_name,
            };

            const requestUrl = `${process.env.REACT_APP_BROADCAST_SERVER}/activity-log/add-single-data`;

            const response = await axiosClient().post(requestUrl, payload, { timeout: 20000 });

            if (response?.data?.success === false) {
                setSubmitError(response?.data?.message || 'Failed to save activity.');
                return;
            }

            setSuccessMsg('✅ Activity saved successfully!');
            resetForm();
        } catch (err: any) {
            console.error('Submit API error:', err);

            const msg = err?.response?.data?.message || err?.message || 'Failed to save activity. Please try again.';

            setSubmitError(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        return () => clearDebounce();
    }, []);

    const descLen = description.trim().length;

    return (
        <>
            <div className="bg-blue-0d3453 min-vh-100 p-4 p-md-5">
                <div className="max-900 mx-auto">
                    <form className="needs-validation" noValidate onSubmit={handleSubmit}>
                        <div className="row g-3">
                            <div className="col-md-12 position-relative">
                                <div className="d-flex justify-content-center mb-3">
                                    <div className="bg-white rounded p-2">
                                        <img src={instituteLogo} style={{ height: 130 }} alt="" />
                                    </div>
                                </div>
                                <div className="max-450 mx-auto">
                                    <h1 className="text-white text-center font-28">{instituteName}</h1>
                                </div>
                                <hr className="max-190 mx-auto text-white" />
                            </div>

                            <div className="col-md-12 position-relative">
                                <h2 className="text-white text-center">Alumni Activity Entry</h2>
                            </div>

                            {/* ✅ SUCCESS / ERROR */}
                            {successMsg ? (
                                <div className="col-12">
                                    <div className="alert alert-success text-center mb-0">{successMsg}</div>
                                </div>
                            ) : null}

                            {submitError ? (
                                <div className="col-12">
                                    <div className="alert alert-danger text-center mb-0">{submitError}</div>
                                </div>
                            ) : null}

                            {/* SEARCH BOX */}
                            <div className="col-md-12 position-relative">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by alumni name"
                                    value={searchText}
                                    onChange={handleInputChange}
                                    onFocus={() => setIsDropdownOpen(true)}
                                    autoComplete="off"
                                    disabled={isSubmitting} 
                                />

                                {errors.searchText ? <div className="text-danger mt-1">{errors.searchText}</div> : null}

                                {isDropdownOpen && !isLoading && (
                                    <div
                                        className="position-absolute w-100 bg-white border rounded shadow-sm"
                                        style={{ zIndex: 10, maxHeight: '250px', overflowY: 'auto' }}>
                                        {/* ✅ Manual Entry FIRST */}
                                        <div
                                            className="d-flex align-items-center p-2 cursor-pointer"
                                            onClick={handleManualEntry}
                                            style={{ background: isManualEntry ? '#f2f2f2' : 'white' }}>
                                            <div className="w-100">
                                                <div className="fw-semibold">Manual Entry</div>
                                                <small className="text-secondary">If alumni not found, add details manually</small>
                                            </div>
                                        </div>

                                        <div className="border-top" />

                                        {isLoading ? (
                                            <div className="p-2 text-center text-secondary">Loading...</div>
                                        ) : searchText.trim() && searchResults.length > 0 ? (
                                            searchResults.map((alumni) => (
                                                <div
                                                    key={alumni.uuid}
                                                    className="d-flex align-items-center p-2 hover-bg-light cursor-pointer"
                                                    onClick={() => handleSelect(alumni)}>
                                                    {alumni.profilePictureFilePath ? (
                                                        <Image
                                                            src={alumni.profilePictureFilePath}
                                                            roundedCircle
                                                            width={30}
                                                            height={30}
                                                            className="me-2"
                                                        />
                                                    ) : null}

                                                    <div className="w-100">
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <div>{getAlumniDisplayName(alumni)}</div>
                                                           
                                                        </div>

                                                        <small className="text-secondary d-block">{getMetaLine(alumni)}</small>
                                                        <small className="text-secondary">{`UUID: ${alumni.uuid}`}</small>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="p-2 text-center text-secondary">Type a name to search...</div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* SELECTED ALUMNI INFO */}
                            {selectedAlumni ? (
                                <div className="col-md-12">
                                    <div className="bg-gray-f2 border rounded p-4">
                                        <div className="row g-3 align-items-center">
                                            {selectedAlumni.profilePictureFilePath ? (
                                                <div className="col-auto">
                                                    <Image
                                                        src={selectedAlumni.profilePictureFilePath}
                                                        roundedCircle
                                                        width={80}
                                                        height={80}
                                                    />
                                                </div>
                                            ) : null}

                                            <div className="col">
                                                <h5 className="mb-1">{getAlumniDisplayName(selectedAlumni)}</h5>
                                                <div className="text-secondary mb-1">{getMetaLine(selectedAlumni)}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : null}

                            {/* MANUAL ENTRY FIELDS */}
                            {isManualEntry ? (
                                <>
                                    <div className="col-md-6">
                                        <label className="form-label mb-1 text-white">
                                            Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={manualName}
                                            disabled={isLoading}
                                            onChange={(e) => {
                                                setManualName(e.target.value);
                                                setErrors((p) => ({ ...p, manualName: undefined }));
                                            }}
                                            placeholder="Full Name"
                                        />
                                        {errors.manualName ? <div className="text-danger mt-1">{errors.manualName}</div> : null}
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label mb-1 text-white">
                                            Email <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            className="form-control"
                                            value={manualEmail}
                                            disabled={isLoading}
                                            onChange={(e) => {
                                                setManualEmail(e.target.value);
                                                setErrors((p) => ({ ...p, manualEmail: undefined }));
                                            }}
                                            placeholder="example@email.com"
                                        />
                                        {errors.manualEmail ? <div className="text-danger mt-1">{errors.manualEmail}</div> : null}
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label mb-1 text-white">
                                            Phone Number <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={manualPhone}
                                            disabled={isLoading}
                                            onChange={(e) => {
                                                setManualPhone(e.target.value);
                                                setErrors((p) => ({ ...p, manualPhone: undefined }));
                                            }}
                                            placeholder="Ex: 9988776655"
                                        />
                                        {errors.manualPhone ? <div className="text-danger mt-1">{errors.manualPhone}</div> : null}
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label mb-1 text-white">
                                            Passout Year <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={manualPassOutYear}
                                            disabled={isLoading}
                                            onChange={(e) => {
                                                setManualPassOutYear(e.target.value);
                                                setErrors((p) => ({ ...p, manualPassOutYear: undefined }));
                                            }}
                                            placeholder="e.g., 2021"
                                        />
                                        {errors.manualPassOutYear ? (
                                            <div className="text-danger mt-1">{errors.manualPassOutYear}</div>
                                        ) : null}
                                    </div>

                                    <div className="col-md-12">
                                        <label className="form-label mb-1 text-white">
                                            Program Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={manualProgramName}
                                            disabled={isLoading}
                                            onChange={(e) => {
                                                setManualProgramName(e.target.value);
                                                setErrors((p) => ({ ...p, manualProgramName: undefined }));
                                            }}
                                            placeholder="Ex: B.Tech, MBA, BBA, etc."
                                        />
                                        {errors.manualProgramName ? (
                                            <div className="text-danger mt-1">{errors.manualProgramName}</div>
                                        ) : null}
                                    </div>
                                </>
                            ) : null}

                            {/* ACTIVITY DATE */}
                            <div className="col-md-4">
                                <label className="form-label mb-1 text-white">Activity Date</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={activityDate}
                                    min={todayDate} // ✅ disables past dates
                                    disabled={isLoading}
                                    onChange={(e) => {
                                        setActivityDate(e.target.value);
                                        setErrors((prev) => ({ ...prev, activityDate: undefined }));
                                    }}
                                />

                                {errors.activityDate ? <div className="text-danger mt-1">{errors.activityDate}</div> : null}
                            </div>

                            {/* CATEGORY */}
                            <div className="col-md-4">
                                <label className="form-label mb-1 text-white">Category</label>
                                <select
                                    className="form-select"
                                    value={category}
                                    disabled={isLoading}
                                    onChange={(e) => {
                                        setCategory(e.target.value);
                                        setErrors((prev) => ({ ...prev, category: undefined }));
                                    }}>
                                    <option value="">Select Category</option>
                                    <option value="PROFILE_UPDATE">Profile Update</option>
                                    <option value="NETWORKING">Networking</option>
                                    <option value="MENTORING_GUIDANCE">Mentoring & Guidance</option>
                                    <option value="RECREATION">Recreation</option>
                                    <option value="RECOGNITION">Recognition</option>
                                    <option value="NOSTALGIA">Nostalgia</option>
                                    <option value="RECRUITMENT">Recruitment</option>
                                    <option value="FUNDRAISE">Fundraise</option>
                                    <option value="GRIEVANCE">Grievance</option>
                                    <option value="SUPPORT_SERVICES">Support Services</option>
                                    <option value="GENERAL_ENGAGEMENT">General Engagement</option>
                                    <option value="OTHERS">Others</option>
                                </select>
                                {errors.category ? <div className="text-danger mt-1">{errors.category}</div> : null}
                            </div>

                            {/* MODE */}
                            <div className="col-md-4">
                                <label className="form-label mb-1 text-white">Mode</label>
                                <select
                                    className="form-select"
                                    value={mode}
                                    disabled={isLoading}
                                    onChange={(e) => {
                                        setMode(e.target.value);
                                        setErrors((prev) => ({ ...prev, mode: undefined }));
                                    }}>
                                    <option value="">Select Mode</option>
                                    <option value="CAMPUS_VISIT">On-Campus Visit</option>
                                    <option value="PHYSICAL_EVENT">Physical Event</option>
                                </select>
                                {errors.mode ? <div className="text-danger mt-1">{errors.mode}</div> : null}
                            </div>

                            {/* DESCRIPTION */}
                            <div className="col-sm-12">
                                <label className="form-label mb-1 text-white">Description</label>
                                <textarea
                                    className="form-control"
                                    rows={8}
                                    placeholder="Description (50–250 characters)"
                                    value={description}
                                    disabled={isLoading}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setDescription(value);

                                        const trimmed = value.trim();
                                        if (!trimmed) {
                                            setErrors((prev) => ({ ...prev, description: 'Description is required' }));
                                        } else if (trimmed.length < 50) {
                                            setErrors((prev) => ({
                                                ...prev,
                                                description: 'Description must be at least 50 characters',
                                            }));
                                        } else if (trimmed.length > 250) {
                                            setErrors((prev) => ({
                                                ...prev,
                                                description: 'Description must not exceed 250 characters',
                                            }));
                                        } else {
                                            setErrors((prev) => ({ ...prev, description: undefined }));
                                        }
                                    }}
                                />

                                <div className="d-flex justify-content-between mt-1">
                                    <div className="text-danger">{errors.description || ''}</div>
                                    <small className={description.trim().length > 250 ? 'text-danger' : 'text-white'}>{descLen}/250</small>
                                </div>
                            </div>
                            {/* ✅ SIMPLE CAPTCHA */}
                            <div className="col-sm-12">
                                <label className="form-label mb-1 text-white">
                                    Captcha <span className="text-danger">*</span>
                                </label>

                                <div className="d-flex gap-2 align-items-center flex-wrap">
                                    <div
                                        className="px-3 py-2 rounded bg-white border fw-bold"
                                        style={{
                                            letterSpacing: '4px',
                                            userSelect: 'none',
                                            fontFamily: 'monospace',
                                            fontSize: '18px',
                                        }}>
                                        {captchaText}
                                    </div>

                                    <button
                                        type="button"
                                        className="btn bg-transparent border-0 bi bi-arrow-clockwise font-32 text-white p-0"
                                        onClick={generateCaptcha}
                                        disabled={isLoading}></button>

                                    {/* <i className="bi bi-arrow-clockwise font-32 text-white cursor-pointer" onClick={generateCaptcha} disabled={isLoading} ></i> */}

                                    <input
                                        type="text"
                                        className="form-control flex-1"
                                        style={{ maxWidth: 220 }}
                                        placeholder="Enter captcha"
                                        value={captchaInput}
                                        disabled={isLoading}
                                        onChange={(e) => {
                                            setCaptchaInput(e.target.value);
                                            setErrors((p) => ({ ...p, captcha: undefined }));
                                        }}
                                    />
                                </div>

                                {errors.captcha ? <div className="text-danger mt-1">{errors.captcha}</div> : null}
                            </div>

                            {/* SAVE BUTTON */}
                            <div className="col-sm-12 text-center">
                                <button type="submit" className="btn btn-green mx-auto max-135" disabled={isSubmitting}>
                                    <span>{isLoading ? 'Saving...' : 'Save'}</span>
                                </button>
                            </div>

                            {/* optional reset button */}
                            {/* <div className="col-sm-12 text-center">
                <button type="button" className="btn btn-light" onClick={resetForm} disabled={isLoading}>
                  Reset
                </button>
              </div> */}
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default AddNewFormModal;
